/**
 * Redimensiona e comprime uma imagem antes do upload.
 *
 * A compressão evita o `HTTP 413 (payload too large)` no envio de fotos tiradas pelo celular,
 * que costumam passar de 5MB. O padrão (largura 1000px / JPEG qualidade 0.7) é o mesmo usado
 * pelo app mobile, para que o cupom fiscal chegue no servidor com o mesmo peso nas duas pontas.
 *
 * @param file Arquivo original escolhido pelo usuário
 * @param larguraMax Largura máxima em px (a altura é proporcional). Imagens menores não são ampliadas
 * @param qualidade Qualidade do JPEG, de 0 a 1
 * @returns Um novo `File` em JPEG. Se a leitura da imagem falhar, devolve o arquivo original
 */
export const comprimirImagem = async (
    file: File,
    larguraMax: number = 1000,
    qualidade: number = 0.7
): Promise<File> => {
    if (!file || !file.type?.startsWith('image/')) return file;

    try {
        const imagem = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });

        const escala = imagem.width > larguraMax ? larguraMax / imagem.width : 1;

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(imagem.width * escala);
        canvas.height = Math.round(imagem.height * escala);

        const contexto = canvas.getContext('2d');
        contexto.drawImage(imagem, 0, 0, canvas.width, canvas.height);

        URL.revokeObjectURL(imagem.src);

        const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((resultado) => resolve(resultado), 'image/jpeg', qualidade);
        });

        if (!blob) return file;

        const nome = file.name?.replace(/\.[^.]+$/, '') || 'imagem';

        return new File([blob], `${nome}.jpg`, { type: 'image/jpeg' });
    } catch (error) {
        console.error('Erro ao comprimir imagem:', error);
        return file;
    }
};

/**
 * Fetches an image from a URL and converts it to a Base64 string.
 * @param url The image URL to fetch.
 * @returns A Promise that resolves to the Base64 string.
 */
export const fetchImageAsBase64 = async (url: string): Promise<string> => {
    if (!url || !url.startsWith("http")) return url;

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Erro ao converter URL para Base64:", error);
        // Retorna string vazia para forçar o fallback para a logo padrão no pdfMake
        return "";
    }
};
